<template lang="pug">
	.app-core--editable-label-field.flex-col.flex-1(:class="{ space: stylingMode === 'filled' }")
		.flex-row.align-center.space(v-if="caption")
			fas(v-if="icon", :icon="icon", prefix="fab")
			.font-small.grey.upper {{caption}}
		EditableLabel.flex-1(ref="label", :disabled="readonly", :editing="editingMode || !textValue")
			slot(v-if="$scopedSlots.display", name="display")
			.font-big(v-else, v-html="htmlValue || caption")
			slot(v-if="$scopedSlots.editor", name="editor", slot="editor")
			.flex-row.align-center.space-medium.flex-1(v-else slot="editor")
				DxDateBox.flex-1(v-if="['date', 'datetime'].includes(type)", ref="dateBox", :stylingMode="stylingMode", :value="dateValue", v-bind="bindOptions", 
					:dropDownOptions="dateDropDownOptions", :placeholder="placeholder", @focusOut="onDateChanged")
				DxNumberBox.flex-1(v-if="type === 'number'", :stylingMode="stylingMode", :value.sync="innerValue", @value-changed="releaseEditing", v-bind="bindOptions", :placeholder="placeholder")
				DxTextArea.flex-1(v-if="type === 'area'", :stylingMode="stylingMode", :value.sync="innerValue", @value-changed="releaseEditing", v-bind="bindOptions", :placeholder="placeholder")
				.flex-row.space.align-center(v-if="['time','datetime'].includes(type)", slot="editor")
					.flex-row.space(v-if="day || (editorOptions && editorOptions.days)")
						.flex-row.small-desc-holder
							.small-desc.upper {{$tCaption('Days')}}
							DxNumberBox.input-align-center(ref="days", :stylingMode="stylingMode", :value.sync="day", v-bind="bindOptions", width="35px", format="#0", :min="0")
						.spacer
					.flex-row.small-desc-holder
						.small-desc.upper {{$tCaption('Hours')}}
						DxNumberBox(:stylingMode="stylingMode", :value.sync="hour", v-bind="bindOptions", width="35px", format="#00", :min="type === 'time' ? 0 : null")
					.grey :
					.flex-row.small-desc-holder
						.small-desc.upper {{$tCaption('Minutes')}}
						DxNumberBox(:stylingMode="stylingMode", :value.sync="minute", v-bind="bindOptions", width="35px", format="#00", :step="editorOptions && editorOptions.minutesStep || 1", :min="type === 'time' ? 0 : null")
					template(v-if="editorOptions && editorOptions.seconds !== false")
						.grey :
						.flex-row.small-desc-holder
							.small-desc.upper {{$tCaption('Seconds')}}
							DxNumberBox(:stylingMode="stylingMode", :value.sync="second", v-bind="bindOptions", width="35px", format="#00", :step="secondsStep || 1", :min="type === 'time' ? 0 : null")
				DxTextBox.flex-1(v-if="!hasSupportedType", :value.sync="innerValue", :stylingMode="stylingMode", @value-changed="releaseEditing", v-bind="bindOptions", :placeholder="placeholder")
				slot(name="end")
</template>

<script>

import EditableLabel from './EditableLabel.vue';
import DxTextArea from 'devextreme-vue/text-area';
import DxTextBox from 'devextreme-vue/text-box';
import DxNumberBox from 'devextreme-vue/number-box';
import DxDateBox from 'devextreme-vue/date-box';

export default {
	props: ['value', 'caption', 'type', 'readonly', 'stylingMode', 'editorOptions', 'icon', 'editingMode', 'secondsStep', 'placeholder', 'preventChangeDateOnTimeChange'],
	components: { EditableLabel, DxTextArea, DxTextBox, DxNumberBox, DxDateBox },
	data() {
		return {
			innerValue: this.value,
			dateDropDownOptions: {
				toolbarItems: [
					{
						widget: 'dxButton',
						toolbar: 'bottom',
						location: 'center',
						options: {
							text: () => this.$tCaption('Today'),
							onClick: () => {
								this.$refs.dateBox.instance.option('value', new Date());
								this.$refs.dateBox.instance.close();
							}
						}
					}
				],
				onHidden: this.onDateChanged
			}
		}
	},
	computed: {
		hasSupportedType() {
			return ['date', 'number', 'area', 'time', 'datetime'].includes(this.type);
		},
		dateValue: {
			get: function() {
				const date = this.$moment(this.innerValue).toDate();
				if (date > new Date(1901,1,1)) {
					return date;
				}
				return void 0;
			},
			set: function(v) {
				if (__DEBUG__)
					console.debug('SetDate', { v })
				this.innerValue = v?.getMonth && v || null;
			},
			cache: false
		},
		timeValue() {
			return this.type === 'time' ? this.$moment().startOf('day').add('seconds', this.innerValue || 0) : (this.dateValue ? this.$moment(this.dateValue) : this.$moment().startOf('day'));
		},
		day: {
			get: function() { return this.timeValue.diff(this.editorOptions?.days ? this.$moment().startOf('day') : this.timeValue.clone().startOf('day'), 'days');  },
			set: function(v) {
				const nowDays = this.day;
				if (__DEBUG__)
					console.debug('Day', { v, nowDays });
				this.innerValue = Math.max(0, this.innerValue + (v - nowDays) * 60 * 60 * 24);
				this.updateTimeValue(); 
			}
		},
		hour: {
			get: function() { return this.timeValue.get('hours'); },
			set: function(v) { 
				if (this.preventChangeDateOnTimeChange && v <= -1) v = 23;
				if (this.preventChangeDateOnTimeChange && v >= 24) v = 0;
				this.timeValue.set('hours', v);
				this.updateTimeValue(); 
			}
		},
		minute: {
			get: function() { return this.timeValue.get('minutes'); },
			set: function(v) {
				if (this.preventChangeDateOnTimeChange && v <= -1) v = 59;
				if (this.preventChangeDateOnTimeChange && v >= 60) v = 0;
				this.timeValue.set('minutes', v);
				this.updateTimeValue();
			}
		},
		second: {
			get: function() { return this.timeValue.get('seconds'); },
			set: function(v) {
				if (this.preventChangeDateOnTimeChange && v <= -1) v = 59;
				if (this.preventChangeDateOnTimeChange && v >= 60) v = 0;
				this.timeValue.set('seconds', v);
				this.updateTimeValue();
			}
		},
		textValue() {
			if (this.type === 'date') return this.dateValue ? this.$moment(this.dateValue).format('LLL') : '';
			return `${this.innerValue || ''}`;
		},
		htmlValue() {
			return this.textValue.replace(/\n/g, '<br>');
		},
		bindOptions() {
			const opts = {};
			if (this.type === 'area') {
				opts.height = 140;
			}
			return {...opts, ...(this.editorOptions || {}) };
		}
	},
	watch: {
		innerValue: {
			handler(v) {
				if (this.$innerChanging) return;
				if (__DEBUG__)
					console.debug('innerValueChange', { v });
				this.$emit('update:value', v);
				this.$emit('change', v);
			},
			deep: true
		},
		value(v) {
			if (`${this.innerValue}` !== `${v}`) {
				this.$innerChanging = true;
				this.innerValue = v;
				this.releaseEditing();
				this.$nextTick(() => this.$innerChanging = false);
			}
		}
	},
	mounted() {
		if (__DEBUG__)
			window.editableLabelField = this;
	},
	destroyed() {
		delete this.$innerChanging;
	},
	methods: {
		onDateChanged() {
			if (!this.$dateValueChecked) {
				const input = this.$refs.dateBox.instance._input()[0];
				input.select();
				const displayValue = getSelection().toString();
				
				const isValid = !isNaN(this.$moment(displayValue).toDate());
				if (!isValid)
					this.dateValue = null;
				else
					this.dateValue = this.$refs.dateBox.instance.option('value');
				
				this.$nextTick(() => {
					this.$dateValueChecked = true;
					getSelection()?.removeAllRanges();
				
					if (__DEBUG__)
						console.debug('onDateValueChange', { v: this.dateValue, isValid, displayValue });
					this.releaseEditing();
					input.blur();
					this.$nextTick(() => delete this.$dateValueChecked);
				});
			}
		},
		updateTimeValue() {
			if (__DEBUG__)
				console.debug('Update time', this.timeValue.toDate());

			if (this.type === 'time') {
				this.innerValue = Math.max(0, this.timeValue.diff(this.editorOptions?.days ? this.$moment().startOf('day') : this.timeValue.clone().startOf('day'), 'seconds'));
			} else {
				this.innerValue = this.dateValue ? this.timeValue.toDate() : null;
			}
		},
		releaseEditing() {
			if (!this.editingMode) {
				this.$refs.label.release();
			}
		}
	}
};
</script>
<style lang="scss">
	@import "main/themes/generated/variables.base.scss";

	.app-core--editable-label-field {
		
	}
</style>
